<template>
  <div
    :style="heroStyle"
    class="hero"
  >
    <div class="hero__layer" />
    <div
      :style="containerStyle"
      class="hero__container"
    >
      <slot name="breadcrumb" />

      <c-title
        :color="titleColor"
        size="h1"
        weight="800"
        :align="alignment"
      >
        <slot name="title" />
      </c-title>

      <c-text
        :color="textColor"
        size="sm"
        weight="400"
        :align="alignment"
      >
        <slot name="description" />
      </c-text>

      <div
        :class="['hero__buttons', `hero__buttons--${alignment}`]"
        @click="pushGTM"
      >
        <slot name="buttons" />
      </div>
    </div>
  </div>
</template>

<script>
import CTitle from '@atoms/title/title.vue'
import CText from '@atoms/text/text.vue'
import isOutbound from '@tagging/isOutbound.js'

export default {
  name: 'c-hero',
  components: { CTitle, CText },
  props: {
    mobileBackgroundImage: {
      type: String,
    },
    tabletBackgroundImage: {
      type: String,
    },
    desktopBackgroundImage: {
      type: String,
    },
    alignment: {
      type: String,
      default: 'center',
    },
    titleColor: {
      type: String,
      default: '#FFFFFF',
    },
    textColor: {
      type: String,
      default: '#FFFFFF',
    },
  },
  data() {
    return {
      isMobile: window.innerWidth <= 480,
      isTablet: window.innerWidth > 480 && window.innerWidth <= 1024,
      hasCarouselAbove: false,
    }
  },
  computed: {
    heroStyle() {
      let backgroundImage

      if (this.isMobile) {
        backgroundImage = this.mobileBackgroundImage
      } else if (this.isTablet) {
        backgroundImage = this.tabletBackgroundImage
      } else {
        backgroundImage = this.desktopBackgroundImage
      }

      return {
        backgroundImage: `url(${backgroundImage})`,
      }
    },
    containerStyle() {
      return {
        width: this.hasCarouselAbove ? '80%' : '100%',
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.checkForCarouselAbove()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 480
      this.isTablet = window.innerWidth > 480 && window.innerWidth <= 1024
    },
    checkForCarouselAbove() {
      const { parentElement } = this.$el
      if (
        parentElement
        && parentElement.classList.contains('carousel__container')
      ) {
        this.hasCarouselAbove = true
      } else {
        this.hasCarouselAbove = false
      }
    },
    titleContent() {
      const slot = this.$slots.title
      if (slot) {
        const slotContent = slot()
        if (slotContent.length > 0 && slotContent[0].children) {
          return slotContent[0].children
        }
      }
      return ''
    },
    pushGTM(event) {
      const target = event.target.closest('a')

      if (target) {
        window.dataLayer.push({
          event: 'click',
          section: 'banner',
          outbound: isOutbound(target),
          name: this.titleContent() || '',
          label: target?.innerText || target?.getAttribute('aria-label') || ''
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.hero {
  align-items: center;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  color: $white;
  display: flex;
  padding: 3.938rem 2.1875rem;
  position: relative;
  justify-content: center;
  width: 100%;

  @media screen and (min-width: 992px) {
    padding: 10.313rem 6.5rem;
  }

  &__layer {
    height: 100%;
    width: 100%;
    background: #2d364866;
    position: absolute;
    z-index: 0;
  }

  &__container {
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
    z-index: 1;
  }

  &__buttons {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    flex-direction: column;
    @include textAlign();

    @media screen and (min-width: 768px) {
      flex-direction: row;
    }
  }

  @include textAlign();
}
</style>
